/**
 * Utility functions for sending emails using our Nodemailer backend
 */

// Change this to your deployed Firebase Functions URL
// For local development, you can use the emulator URL
// For production, this should be your deployed function URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://us-central1-nstp20-a5a4f.cloudfunctions.net/emailService' 
  : 'http://localhost:5001/nstp20-a5a4f/us-central1/emailService';

// Removed local server fallback as we're using Firebase Functions now
// const FALLBACK_API_URL = 'http://localhost:3001/api';

// Helper to check if a server is available
// const checkServerAvailability = async (url) => {
//   try {
//     const response = await fetch(url, { 
//       method: 'HEAD',
//       timeout: 3000 
//     });
//     return response.ok;
//   } catch (error) {
//     console.log(`Server at ${url} is not available:`, error.message);
//     return false;
//   }
// };

/**
 * Send a welcome email to a newly registered student
 * 
 * @param {Object} studentData - The student data object
 * @returns {Promise} - Promise resolving to the API response
 */
export const sendWelcomeEmail = async (studentData) => {
  try {
    const { email, firstName, lastName, nstpComponent } = studentData;
    
    if (!email || !firstName || !lastName || !nstpComponent) {
      throw new Error('Missing required student data for sending welcome email');
    }
    
    console.log('Sending welcome email to:', email);
    
    const response = await fetch(`${API_BASE_URL}/send-welcome-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        firstName,
        lastName,
        nstpComponent,
      }),
    });
    
    let data;
    try {
      data = await response.json();
    } catch (err) {
      console.error('Error parsing welcome email response:', err);
      throw new Error('Invalid response from email server');
    }
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send welcome email');
    }
    
    return data;
  } catch (error) {
    console.error('Error sending welcome email:', error);
    throw error;
  }
};

/**
 * Send a general email
 * 
 * @param {Object} emailData - The email data object
 * @param {string} emailData.to - Recipient email address
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.message - Email HTML content
 * @returns {Promise} - Promise resolving to the API response
 */
export const sendEmail = async (emailData) => {
  try {
    const { to, subject, message } = emailData;
    
    if (!to || !subject || !message) {
      throw new Error('Missing required email data');
    }
    
    const response = await fetch(`${API_BASE_URL}/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject,
        message,
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send email');
    }
    
    return data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

/**
 * Send bulk emails to multiple recipients
 * 
 * @param {Array} recipients - Array of recipient objects
 * @param {string} subject - Email subject
 * @param {string|Function} template - HTML template or function that returns HTML
 * @returns {Promise} - Promise resolving to array of results
 */
export const sendBulkEmails = async (recipients, subject, template) => {
  try {
    if (!Array.isArray(recipients) || recipients.length === 0) {
      throw new Error('Recipients must be a non-empty array');
    }
    
    if (!subject) {
      throw new Error('Subject is required');
    }
    
    if (!template) {
      throw new Error('Email template is required');
    }
    
    // If template is a function, we need to generate HTML for each recipient
    let finalTemplate = template;
    if (typeof template === 'function') {
      // Example recipient to generate template
      const exampleRecipient = recipients[0];
      finalTemplate = template(exampleRecipient);
    }
    
    const response = await fetch(`${API_BASE_URL}/send-bulk-emails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recipients,
        subject,
        template: finalTemplate,
        useCustomTemplate: typeof template === 'function',
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send bulk emails');
    }
    
    return data;
  } catch (error) {
    console.error('Error sending bulk emails:', error);
    throw error;
  }
};

/**
 * Check if email configuration is properly set up
 * 
 * @returns {Promise} - Promise resolving to the email configuration status
 */
export const checkEmailConfig = async () => {
  try {
    // Try connecting to the serverless API
    const statusCheck = await fetch(`${API_BASE_URL}/status`);
    
    // If this fails, it will go to the catch block
    const statusData = await statusCheck.json();
    
    return {
      isConfigured: statusData.isConfigured,
      status: statusData.status,
      message: statusData.message
    };
  } catch (error) {
    console.error('Error checking email configuration:', error);
    return {
      isConfigured: false,
      status: 'error',
      message: 'Could not connect to email service. Please check Firebase Function configuration.'
    };
  }
};

/**
 * Update email configuration
 * Note: This won't work with the serverless approach as we need to set
 * environment variables in Firebase Functions using the CLI
 */
export const updateEmailConfig = async (configData) => {
  try {
    throw new Error(
      'Email configuration updates are handled through Firebase CLI. ' +
      'Please update your email settings using: firebase functions:config:set email.user="..." email.pass="..."'
    );
  } catch (error) {
    console.error('Error updating email configuration:', error);
    throw error;
  }
};

/**
 * Generate a welcome email template for newly registered students
 * 
 * @param {Object} data - Template data
 * @param {string} data.firstName - Student's first name
 * @param {string} data.lastName - Student's last name
 * @param {string} data.nstpComponent - NSTP component (ROTC, CWTS, LTS)
 * @returns {string} - HTML email template
 */
export const generateWelcomeEmailTemplate = (data) => {
  const { firstName, lastName, nstpComponent } = data;
  
  // Get component details
  let componentDetails = '';
  let componentColor = '#4CAF50'; // Default green
  
  if (nstpComponent === 'CWTS') {
    componentDetails = 'Civic Welfare Training Service (CWTS)';
    componentColor = '#2196F3'; // Blue
  } else if (nstpComponent === 'ROTC') {
    componentDetails = 'Reserve Officers\' Training Corps (ROTC)';
    componentColor = '#F44336'; // Red
  } else if (nstpComponent === 'LTS') {
    componentDetails = 'Literacy Training Service (LTS)';
    componentColor = '#FF9800'; // Orange
  }
  
  const today = new Date().toLocaleDateString();
  
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
      <div style="background-color: ${componentColor}; padding: 15px; text-align: center; border-radius: 5px 5px 0 0;">
        <h1 style="color: white; margin: 0;">NSTP Registration Confirmation</h1>
      </div>
      
      <div style="padding: 20px;">
        <p style="font-size: 16px;">Dear <strong>${firstName} ${lastName}</strong>,</p>
        
        <p>Thank you for registering for the National Service Training Program (NSTP).</p>
        
        <div style="background-color: #f9f9f9; border-left: 4px solid ${componentColor}; padding: 15px; margin: 20px 0;">
          <p style="margin: 0;"><strong>Program:</strong> ${componentDetails}</p>
          <p style="margin: 10px 0 0;"><strong>Registration Date:</strong> ${today}</p>
        </div>
        
        <p>You have successfully registered for the NSTP program. Important information to know:</p>
        
        <ul style="line-height: 1.6;">
          <li>Orientation will be held on the first week of classes</li>
          <li>Please bring your registration confirmation</li>
          <li>Uniform guidelines will be provided during orientation</li>
          <li>Class schedules will be posted on the NSTP bulletin board</li>
        </ul>
        
        <p>If you have any questions or need further assistance, please don't hesitate to contact us at <a href="mailto:nstp@example.com" style="color: ${componentColor};">nstp@example.com</a> or call our office at (123) 456-7890.</p>
        
        <p style="margin-top: 30px;">Best regards,</p>
        <p><strong>NSTP Administration Team</strong></p>
      </div>
      
      <div style="background-color: #f5f5f5; padding: 15px; text-align: center; font-size: 12px; color: #666; border-radius: 0 0 5px 5px;">
        <p style="margin: 0;">This is an automated message. Please do not reply to this email.</p>
        <p style="margin: 5px 0 0;">© ${new Date().getFullYear()} National Service Training Program. All rights reserved.</p>
      </div>
    </div>
  `;
};

/**
 * Generate a verification email template
 * 
 * @param {Object} data - Template data
 * @param {string} data.firstName - User's first name
 * @param {string} data.verificationLink - Verification link
 * @returns {string} - HTML email template
 */
export const generateVerificationEmailTemplate = (data) => {
  const { firstName, verificationLink } = data;
  
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
      <h2 style="color: #333; text-align: center;">NSTP Email Verification</h2>
      <p>Dear ${firstName},</p>
      <p>Thank you for registering for the National Service Training Program (NSTP).</p>
      <p>Please verify your email address by clicking the button below:</p>
      <div style="text-align: center; margin: 30px 0;">
        <a href="${verificationLink}" style="background-color: #4CAF50; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">Verify Email</a>
      </div>
      <p>If the button doesn't work, you can copy and paste the following link into your browser:</p>
      <p style="word-break: break-all;">${verificationLink}</p>
      <p style="margin-top: 20px;">Best regards,</p>
      <p><strong>NSTP Administration Team</strong></p>
    </div>
  `;
};

/**
 * Generate a notification email template
 * 
 * @param {Object} data - Template data
 * @param {string} data.firstName - User's first name
 * @param {string} data.subject - Notification subject
 * @param {string} data.message - Notification message
 * @returns {string} - HTML email template
 */
export const generateNotificationEmailTemplate = (data) => {
  const { firstName, subject, message } = data;
  
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
      <h2 style="color: #333; text-align: center;">NSTP Notification</h2>
      <p>Dear ${firstName},</p>
      <h3>${subject}</h3>
      <div>${message}</div>
      <p style="margin-top: 20px;">Best regards,</p>
      <p><strong>NSTP Administration Team</strong></p>
    </div>
  `;
};

/**
 * Generate an announcement email template
 * 
 * @param {Object} data - Template data
 * @param {string} data.title - Announcement title
 * @param {string} data.content - Announcement content
 * @param {string} data.category - Announcement category
 * @param {string} data.imageBase64 - Optional image in base64 format
 * @returns {string} - HTML email template
 */
export const generateAnnouncementEmailTemplate = (data) => {
  const { title, content, category, imageBase64 } = data;
  
  // Get category color
  let categoryColor = '#4CAF50'; // Default green
  
  if (category === 'CWTS') {
    categoryColor = '#2196F3'; // Blue
  } else if (category === 'ROTC') {
    categoryColor = '#F44336'; // Red
  } else if (category === 'LTS') {
    categoryColor = '#FF9800'; // Orange
  } else if (category === 'Important') {
    categoryColor = '#9C27B0'; // Purple
  } else if (category === 'Event') {
    categoryColor = '#009688'; // Teal
  }
  
  // Format date
  const today = new Date().toLocaleDateString();
  
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
      <div style="background-color: ${categoryColor}; padding: 15px; text-align: center; border-radius: 5px 5px 0 0;">
        <h1 style="color: white; margin: 0;">NSTP Announcement</h1>
      </div>
      
      <div style="padding: 20px;">
        <div style="background-color: #f9f9f9; border-left: 4px solid ${categoryColor}; padding: 15px; margin: 20px 0;">
          <h2 style="margin-top: 0; color: #333;">${title}</h2>
          <p style="margin: 0;"><strong>Category:</strong> ${category}</p>
          <p style="margin: 5px 0 0;"><strong>Date:</strong> ${today}</p>
        </div>
        
        <div style="margin: 20px 0; line-height: 1.6;">
          ${content.replace(/\n/g, '<br>')}
        </div>
        
        ${imageBase64 ? `
          <div style="margin: 20px 0; text-align: center;">
            <img src="${imageBase64}" alt="Announcement Image" style="max-width: 100%; max-height: 300px; border-radius: 5px;">
          </div>
        ` : ''}
        
        <p style="margin-top: 30px;">For more information, please log in to the NSTP portal or contact the administration office.</p>
        
        <p style="margin-top: 20px;">Best regards,</p>
        <p><strong>NSTP Administration Team</strong></p>
      </div>
      
      <div style="background-color: #f5f5f5; padding: 15px; text-align: center; font-size: 12px; color: #666; border-radius: 0 0 5px 5px;">
        <p style="margin: 0;">This is an automated message. Please do not reply to this email.</p>
        <p style="margin: 5px 0 0;">© ${new Date().getFullYear()} National Service Training Program. All rights reserved.</p>
      </div>
    </div>
  `;
};

/**
 * Send announcement notification emails to all registered students
 * 
 * @param {Object} announcementData - The announcement data
 * @param {Array} recipients - Array of recipient email addresses
 * @returns {Promise} - Promise resolving to the API response
 */
export const sendAnnouncementNotification = async (announcementData, recipients) => {
  try {
    if (!announcementData || !Array.isArray(recipients) || recipients.length === 0) {
      throw new Error('Missing announcement data or recipients list is empty');
    }
    
    // Check if server is available first
    let statusData;
    try {
      console.log('Checking email server status...');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const statusCheck = await fetch(`${API_BASE_URL}/status`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!statusCheck.ok) {
        console.error('Email server status check failed with status:', statusCheck.status);
        throw new Error(`Email server returned status ${statusCheck.status}`);
      }
      
      statusData = await statusCheck.json();
      console.log('Email server status:', statusData);
      
      if (!statusData.email || !statusData.email.configured) {
        throw new Error('Email server is not properly configured. Please set up email credentials in the Email Settings page.');
      }
      
      if (!statusData.email.connectionVerified) {
        throw new Error(`Email SMTP connection failed: ${statusData.email.error || 'Unknown error'}. Please check your email credentials.`);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.error('Connection timeout when checking email server');
        throw new Error('Connection timeout. The email server is not responding. Please ensure it is running.');
      }
      
      console.error('Email server check failed:', err);
      throw new Error(`Email server is not available: ${err.message}. Please ensure the server is running and email is configured.`);
    }
    
    console.log(`Sending announcement notification to ${recipients.length} recipients`);
    
    // Generate email content
    const emailTemplate = generateAnnouncementEmailTemplate(announcementData);
    
    // Prepare recipients in the format expected by the API
    const recipientsList = recipients.map(email => ({ email }));
    
    console.log(`Making request to ${API_BASE_URL}/send-bulk-emails with ${recipientsList.length} recipients`);
    
    const response = await fetch(`${API_BASE_URL}/send-bulk-emails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recipients: recipientsList,
        subject: `NSTP Announcement: ${announcementData.title}`,
        template: emailTemplate,
        useCustomTemplate: false,
      }),
    });
    
    console.log('Bulk email response status:', response.status);
    
    let data;
    try {
      data = await response.json();
      console.log('Bulk email response data:', data);
    } catch (err) {
      console.error('Error parsing bulk email response:', err);
      throw new Error(`Invalid response from email server: ${err.message}`);
    }
    
    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to send announcement notifications';
      console.error('Error in server response:', errorMessage);
      throw new Error(errorMessage);
    }
    
    return data;
  } catch (error) {
    console.error('Error sending announcement notifications:', error);
    throw error;
  }
};

/**
 * Test the email connection
 * 
 * @returns {Promise} - Promise resolving to the test result
 */
export const testEmailConnection = async () => {
  try {
    // Try connecting to the serverless API status endpoint
    const statusCheck = await fetch(`${API_BASE_URL}/status`);
    
    if (!statusCheck.ok) {
      return {
        success: false,
        message: `Email service returned status ${statusCheck.status}. Make sure your Firebase Functions are deployed.`,
      };
    }
    
    const statusData = await statusCheck.json();
    
    if (!statusData.isConfigured) {
      return {
        success: false,
        message: `Email service is not configured. Please set your email credentials using the Firebase CLI: firebase functions:config:set email.user="..." email.pass="..."`,
      };
    }
    
    return {
      success: true,
      message: 'Successfully connected to email service',
    };
  } catch (error) {
    console.error('Error testing email connection:', error);
    return {
      success: false,
      message: `Error connecting to email service: ${error.message}. Make sure you have:
1. Upgraded to Firebase Blaze plan
2. Deployed your Firebase Functions
3. Set your email credentials with 'firebase functions:config:set'`,
    };
  }
}; 